import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PeranakanDesignTiles.scss';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setExperienceLive, ExhibitId } from '../../../store/slices/exhibitSlice';
import { usePeranakanDesignZone } from '../../../hooks/useZoneStatus';
import TcPopupR65 from '../../TcPopupR65';
import { useDeviceDetection } from '../../../hooks/useDeviceDetection';
import { getVisitorIdWithFallback } from '../../../services/apiService';
import { ZONE_STATUS_POLLING_INTERVAL_MS, getZonePollingIntervalSeconds } from '../../../utils/constants';


export default function PeranakanDesignTiles({ allImages }: { allImages: string[] }) {
    const { t } = useTranslation();
    const { exhibitId } = useParams<{ exhibitId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [
        imagePeranakanTilesBg, 
        imagePeranakanTile01, 
        imagePeranakanTile02, 
        imagePeranakanTile03, 
        imagePeranakanTile04
    ] = allImages;
    
    // React ref for the tile wrapper element
    const tileWrapperRef = useRef<HTMLDivElement>(null);
    
    // Get current user's visitorId from Redux (with fallback)
    const { visitorId: reduxVisitorId, name, email, isAuthenticated, hasCompletedRegistration } = useAppSelector(state => state.user);
    
    // Use enhanced fallback strategy for visitor ID
    const visitorId = getVisitorIdWithFallback() || undefined;
    
    const [selectedTileId, setSelectedTileId] = useState<string | null>(null);
    const [displayedTile, setDisplayedTile] = useState<string | null>(null);

    const [showZoneBusyPopup, setShowZoneBusyPopup] = useState(false);
    const [busyPopupCountdown, setBusyPopupCountdown] = useState(20);
    const [isCountdownActive, setIsCountdownActive] = useState(false);
    const [showZoneTimeoutPopup, setShowZoneTimeoutPopup] = useState(false);
    const [userHasActiveSession, setUserHasActiveSession] = useState(false);

    // State for QR scan processing
    const [qrScanProcessed, setQrScanProcessed] = useState(false);
    const qrScanClaimInitiated = useRef(false);

    // Button cooldown state to prevent button bashing
    const [buttonCooldown, setButtonCooldown] = useState(false);
    const [cooldownTimer, setCooldownTimer] = useState<NodeJS.Timeout | null>(null);
    const BUTTON_COOLDOWN_DURATION = 500; // 0.5 second to prevent button bashing

    // Zone timeout timer (separate from global exhibit timeout)
    const [zoneTimeoutTimer, setZoneTimeoutTimer] = useState<NodeJS.Timeout | null>(null);
    const ZONE_TIMEOUT_DURATION = 5 * 60 * 1000; // 5 minutes for zone timeout

    // Zone status polling system (configurable interval, default 4 seconds)
    // Use refs instead of state to avoid React Strict Mode issues
    const statusPollingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [isPollingActive, setIsPollingActive] = useState(false);

    // Track if QR scan has been processed to avoid duplicate processing
    const qrScanProcessedRef = useRef<string | null>(null);

    // Zone status management for Peranakan Design zone (10.3)
    const {
        zoneStatus,
        isLoading: zoneLoading,
        error: zoneError,
        isAvailable,
        isProcessing,
        fetchZoneStatus,
        claimZone,
        updatePayload,
        updateStatus,
        clearError: clearZoneError,
    } = usePeranakanDesignZone(visitorId);

    // Device detection
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';
    
    // Zone ownership check - only registered users (with visitorId) can claim and interact
    const isClaimedByCurrentUser = visitorId && zoneStatus?.claimedBy === visitorId;
    
    // Zone availability check - but respect user session even during conflicts
    const isAvailableToClaim = isAvailable && !isClaimedByCurrentUser && !userHasActiveSession;
    
    // Enhanced interaction permission that considers user session during conflicts
    const canUserInteract = () => {
        // For registered users: check if zone is claimed by them OR they have an active session
        if (visitorId) {
            return isClaimedByCurrentUser || userHasActiveSession;
        }
        // For non-registered users: check if they have an active session
        return userHasActiveSession;
    };
    
    // Force re-render when wrapper ref becomes available
    const [wrapperReady, setWrapperReady] = useState(false);
    
    useEffect(() => {
        if (tileWrapperRef.current && !wrapperReady) {
            setWrapperReady(true);
        }
    }, [wrapperReady]);
    
    
    // Tile button data - mapping based on the design layout
    const tileButtons = [
        {
            id: 'tile01',
            image: imagePeranakanTile01,
            title: t('exhibits.peranakanDesign.patterns.geometric.title'),
            content: t('exhibits.peranakanDesign.patterns.geometric.description')
        },
        {
            id: 'tile02',
            image: imagePeranakanTile02,
            title: t('exhibits.peranakanDesign.patterns.floral.title'),
            content: t('exhibits.peranakanDesign.patterns.floral.description')
        },
        {
            id: 'tile03',
            image: imagePeranakanTile03,
            title: t('exhibits.peranakanDesign.patterns.phoenixPeony.title'),
            content: t('exhibits.peranakanDesign.patterns.phoenixPeony.description')
        },
        {
            id: 'tile04',
            image: imagePeranakanTile04,
            title: t('exhibits.peranakanDesign.patterns.kuehKueh.title'),
            content: t('exhibits.peranakanDesign.patterns.kuehKueh.description')
        }
    ];

    // Handle tile button clicks with zone payload updates (following diagram flow)
    const handleTileClick = async (tileId: string, tileTitle: string, optionValue: number) => {
        // Check if buttons are in cooldown period
        if (buttonCooldown) {
            console.log('⏸️ Button cooldown active - ignoring click');
            return;
        }

        // Check: Zone must be claimed by current user (or active session for anonymous users)
        // For registered users: check if zone is claimed by them OR they have an active session
        // For non-registered users: check if they have an active session (meaning they've scanned QR and claimed the zone)
        const canInteract = canUserInteract();
        
        if (!canInteract) {
            console.log('❌ Cannot interact with tiles - zone claimed by another user');
            setShowZoneBusyPopup(true);
            setBusyPopupCountdown(20);
            setIsCountdownActive(false);
            return;
        }

        console.log(`🎯 Tile clicked: ${tileId} with option: ${optionValue}`);

        // Start button cooldown immediately to prevent rapid clicks
        startButtonCooldown();

        // Check if option tile pressed? (from diagram)
        if (optionValue > 0) {
            try {
                // PUT zone-status/{zone_id}/payload with option = option_id (int)
                console.log('🔍 Before updatePayload - Current zone status:', {
                    status: zoneStatus?.status,
                    claimedBy: zoneStatus?.claimedBy,
                    isClaimedByCurrentUser,
                    isAvailable,
                    userHasActiveSession,
                    canInteract,
                    visitorId
                });

                // Additional pre-flight validation
                const preFlightCheck = {
                    hasVisitorId: !!visitorId,
                    zoneClaimedByUser: zoneStatus?.claimedBy === visitorId,
                    userHasSession: userHasActiveSession,
                    canProceed: (zoneStatus?.claimedBy === visitorId) || userHasActiveSession
                };
                
                console.log('🛂 Pre-flight validation for payload update:', preFlightCheck);
                
                if (!preFlightCheck.canProceed) {
                    console.warn('❌ Pre-flight check failed - aborting payload update');
                    setShowZoneBusyPopup(true);
                    setBusyPopupCountdown(20);
                    setIsCountdownActive(false);
                    return;
                }

                const payloadResult = await updatePayload({
                    payload: {
                        option: optionValue,
                    },
                });

                console.log('🔍 After updatePayload - API response:', payloadResult);
                console.log('🔍 After updatePayload - Updated zone status:', {
                    status: zoneStatus?.status,
                    claimedBy: zoneStatus?.claimedBy,
                    isClaimedByCurrentUser,
                    isAvailable,
                    userHasActiveSession,
                    canInteract: canUserInteract(),
                    visitorId
                });

                console.log(`✅ Zone payload updated with option: ${optionValue}`);

                // Update selected state for highlighting (highlight option)
                setSelectedTileId(tileId);
                setDisplayedTile(tileId);

                // Dispatch experience live status
                dispatch(setExperienceLive(true));

                // Reset zone timeout since user is actively interacting
                resetZoneTimeout();
                startZoneTimeout();

            } catch (error: any) {
                console.error(`❌ Failed to update zone payload with option ${optionValue}:`, error);
                
                // Handle specific error types
                if (error?.status === 409) {
                    console.warn('⚠️ Zone payload update conflict (409) - maintaining user session');
                    
                    // For 409 conflicts, maintain user session and UI state regardless of API response
                    console.log('🔄 Maintaining user session and UI state during 409 conflict');
                    
                    // Ensure user session remains active
                    if (!userHasActiveSession) {
                        setUserHasActiveSession(true);
                        console.log('✅ User session activated to maintain interaction during conflict');
                    }
                    
                    // Still update UI optimistically since user interaction was valid
                    setSelectedTileId(tileId);
                    setDisplayedTile(tileId);
                    dispatch(setExperienceLive(true));
                    resetZoneTimeout();
                    startZoneTimeout();
                    
                    console.log('✅ UI state maintained during 409 conflict - user can continue interacting');
                    
                } else {
                    // Show error popup for other errors
                    setShowZoneBusyPopup(true);
                    setBusyPopupCountdown(10);
                    setIsCountdownActive(false);
                }
            }
        } else {
            // Option == 0 case (unhighlight option)
            console.log('🔄 Unhighlighting option (option = 0)');
            
            try {
                console.log('🔍 Before unhighlight updatePayload - Current zone status:', {
                    status: zoneStatus?.status,
                    claimedBy: zoneStatus?.claimedBy,
                    isClaimedByCurrentUser,
                    isAvailable,
                    visitorId
                });

                // PUT zone-status/{zone_id}/payload with option = 0
                const unhighlightResult = await updatePayload({
                    payload: {
                        option: 0,
                    },
                });

                console.log('🔍 After unhighlight updatePayload - API response:', unhighlightResult);
                console.log('🔍 After unhighlight updatePayload - Updated zone status:', {
                    status: zoneStatus?.status,
                    claimedBy: zoneStatus?.claimedBy,
                    isClaimedByCurrentUser,
                    isAvailable,
                    visitorId
                });

                // Unhighlight option
                setSelectedTileId(null);
                setDisplayedTile(null);

                // Dispatch experience live status
                dispatch(setExperienceLive(false));

                // GET zone-status/{zone_id} (as per diagram when option == 0)
                await fetchZoneStatus();
                console.log('🔄 Zone status refreshed');

            } catch (error: any) {
                console.error('❌ Failed to unhighlight option:', error);
                
                // Handle specific error types
                if (error?.status === 409) {
                    console.warn('⚠️ Zone payload unhighlight conflict (409) - maintaining user session');
                    
                    // For 409 conflicts, maintain user session regardless of API response
                    console.log('🔄 Maintaining user session during unhighlight 409 conflict');
                    
                    // Ensure user session remains active
                    if (!userHasActiveSession) {
                        setUserHasActiveSession(true);
                        console.log('✅ User session activated to maintain interaction during unhighlight conflict');
                    }
                    
                    // Still update UI optimistically
                    setSelectedTileId(null);
                    setDisplayedTile(null);
                    dispatch(setExperienceLive(false));
                    
                    // Try to refresh zone status anyway
                    try {
                        await fetchZoneStatus();
                        console.log('🔄 Zone status refreshed after unhighlight conflict');
                    } catch (refreshError) {
                        console.warn('⚠️ Zone status refresh also failed:', refreshError);
                    }
                    
                    console.log('✅ UI state maintained during unhighlight 409 conflict');
                }
            }
        }
    };

    // Start button cooldown to prevent button bashing
    const startButtonCooldown = () => {
        // Clear any existing cooldown timer
        if (cooldownTimer) {
            clearTimeout(cooldownTimer);
        }

        // Set cooldown active
        setButtonCooldown(true);
        console.log('⏸️ Button cooldown started - 5 seconds until next click allowed');

        // Set timer to end cooldown
        const timer = setTimeout(() => {
            setButtonCooldown(false);
            setCooldownTimer(null);
            console.log('✅ Button cooldown ended - clicks allowed again');
        }, BUTTON_COOLDOWN_DURATION);

        setCooldownTimer(timer);
    };

    const handleCloseZoneBusyPopup = () => {
        setShowZoneBusyPopup(false);
        // Don't reset countdown - let it continue running in background
        console.log('🔕 Zone busy popup closed manually - countdown continues in background');
    };

    const handleCloseZoneTimeoutPopup = async () => {
        setShowZoneTimeoutPopup(false);
        // Reset user session when timeout popup is closed
        setUserHasActiveSession(false);
        // Reset selected tile state to return all tiles to normal state
        setSelectedTileId(null);
        // Reset displayed tile to default
        setDisplayedTile(null);
        
        // Stop zone status polling since session ended
        stopStatusPolling();
        
        // Reset QR scan claim flag to allow new QR scans
        qrScanClaimInitiated.current = false;
        setQrScanProcessed(false);
        
        console.log('⏰ Zone timeout - resetting user session, tile states, polling, and QR scan flags');
        
        // Reset zone timeout timer for next interaction
        if (zoneTimeoutTimer) {
            clearTimeout(zoneTimeoutTimer);
            setZoneTimeoutTimer(null);
        }

        // Reset experience live status
        dispatch(setExperienceLive(false));
        
        // IMPORTANT: Explicitly release the zone by updating status to "available"
        try {
            console.log('🔓 Releasing zone - updating status to available...');
            await updateStatus({
                status: 'available',
                claimedBy: undefined,
                payload: { option: 0 }
            });
            console.log('✅ Zone released successfully - status updated to available');
            
            // NOTE: Do NOT refresh zone status after timeout - polling has been stopped
            // and user session should be completely ended. Refreshing would trigger
            // useEffect hooks that might restart the session.
            console.log('⚠️ Skipping zone status refresh - user session ended, polling stopped');
            
        } catch (error) {
            console.error('❌ Failed to release zone:', error);
            // NOTE: Do NOT refresh zone status after error either - same reason as above
            console.log('⚠️ Zone release failed but skipping status refresh - user session ended');
        }
    };

    // Start zone timeout when zone is claimed (processing)
    const startZoneTimeout = useCallback(() => {
        // Clear any existing timer
        if (zoneTimeoutTimer) {
            clearTimeout(zoneTimeoutTimer);
        }
        
        // Start new timeout
        const timer = setTimeout(() => {
            setShowZoneTimeoutPopup(true);
            setUserHasActiveSession(false);
            setZoneTimeoutTimer(null);
            
            // IMPORTANT: Stop zone status polling immediately when timeout occurs
            // Use ref to get the latest stopStatusPolling function
            stopStatusPollingRef.current();
            
            // Reset QR scan claim flag to allow new QR scans after timeout
            qrScanClaimInitiated.current = false;
            setQrScanProcessed(false);
            
            console.log('⏰ Zone timeout occurred - resetting user session, stopping polling, and resetting QR scan flags');
        }, ZONE_TIMEOUT_DURATION);
        
        setZoneTimeoutTimer(timer);
        console.log('🕐 Zone timeout started - user has 5 minutes to interact');
    }, [zoneTimeoutTimer]);

    // Reset zone timeout on user interaction
    const resetZoneTimeout = () => {
        if (zoneTimeoutTimer) {
            clearTimeout(zoneTimeoutTimer);
            setZoneTimeoutTimer(null);
            console.log('🔄 Zone timeout reset due to user interaction');
        }
    };

    // Start zone status polling (every 2 seconds as per diagram)
    const startStatusPolling = useCallback(() => {
        // IMPORTANT: Clear any existing polling timer first
        if (statusPollingTimerRef.current) {
            console.log('🛑 Clearing existing polling timer before starting new one, ID:', statusPollingTimerRef.current);
            clearInterval(statusPollingTimerRef.current);
            statusPollingTimerRef.current = null;
        }
        
        setIsPollingActive(true);
        console.log(`🔄 Starting zone status polling every ${getZonePollingIntervalSeconds()} seconds...`);
        
        // Start polling interval
        const timer = setInterval(async () => {
            try {
                console.log('📡 Polling zone status...');
                await fetchZoneStatus();
            } catch (error) {
                console.error('❌ Failed to poll zone status:', error);
                // Don't stop polling on error - continue trying
            }
        }, ZONE_STATUS_POLLING_INTERVAL_MS);
        
        statusPollingTimerRef.current = timer;
        console.log('✅ Zone status polling started with timer ID:', timer);
    }, [fetchZoneStatus]);

    // Stop zone status polling
    const stopStatusPolling = useCallback(() => {
        if (statusPollingTimerRef.current) {
            console.log('⏹️ Zone status polling stopped, clearing timer ID:', statusPollingTimerRef.current);
            clearInterval(statusPollingTimerRef.current);
            statusPollingTimerRef.current = null;
            setIsPollingActive(false);
        } else {
            console.log('⏹️ No polling timer to stop');
        }
    }, []);
    
    // Use a ref for the latest stopStatusPolling function to avoid stale closures
    const stopStatusPollingRef = useRef(stopStatusPolling);
    stopStatusPollingRef.current = stopStatusPolling;

    // Reset status polling (stop and start again)
    const resetStatusPolling = useCallback(() => {
        stopStatusPolling();
        // Small delay before restarting to avoid conflicts
        setTimeout(() => {
            startStatusPolling();
        }, 100);
    }, [stopStatusPolling, startStatusPolling]);

    // Auto-start zone session management if zone is already claimed by current user
    useEffect(() => {
        // Check if zone is claimed by current user but we don't have an active session yet
        if (isClaimedByCurrentUser && !userHasActiveSession && !isPollingActive) {
            console.log('🎯 Zone already claimed by current user - starting session management');
            
            // Set user session active
            setUserHasActiveSession(true);
            
            // Start zone timeout
            setTimeout(() => {
                startZoneTimeout();
                dispatch(setExperienceLive(true));
            }, 100);
            
            // Start zone status polling
            setTimeout(() => {
                startStatusPolling();
            }, 300);
            
            console.log('✅ Zone session management started for already claimed zone');
        }
    }, [isClaimedByCurrentUser, userHasActiveSession, isPollingActive, startZoneTimeout, startStatusPolling, dispatch]);

    // Handle QR scan automatic zone claiming according to diagram flow
    useEffect(() => {
        const handleQrScanEffect = async () => {
            const fromQrScan = searchParams.get('fromQrScan');
            const qrTimestamp = searchParams.get('qrTimestamp');
            
            // Note: QR scan claiming is now handled in PeranakanDesign component
            // This component only handles tile interactions for already claimed zones
            if (fromQrScan === 'true') {
                // Clear the parameters from URL since they're no longer needed
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('fromQrScan');
                newSearchParams.delete('qrTimestamp');
                setSearchParams(newSearchParams, { replace: true });
            }
        };
        
        handleQrScanEffect();
    }, [searchParams, visitorId, setSearchParams, isClaimedByCurrentUser, userHasActiveSession, canUserInteract, zoneStatus, qrScanProcessed, qrScanClaimInitiated]);

    // Helper function to calculate tile positions based on wrapper dimensions
    const getTilePositions = (tileSize: number = 80, spacing: number = 20) => {
        // First try to use the React ref (most reliable)
        let tileWrapper: HTMLElement | null = tileWrapperRef.current;
        let foundMethod = 'ref';
        
        // Fallback to DOM queries if ref is not available
        if (!tileWrapper) {
            tileWrapper = document.querySelector('.penarakan-design-tile-wrapper');
            foundMethod = 'querySelector(.penarakan-design-tile-wrapper)';
        }
        if (!tileWrapper) {
            tileWrapper = document.querySelector('.penarakan-design-tile-container');
            foundMethod = 'querySelector(.penarakan-design-tile-container)';
        }
        if (!tileWrapper) {
            tileWrapper = document.querySelector('.penarakan-design-tile-wrapper-bg');
            foundMethod = 'querySelector(.penarakan-design-tile-wrapper-bg)';
        }
        
        
        if (!tileWrapper) {
            console.warn('⚠️ penarakan-design-tile-wrapper element not found, using fallback positioning');
            // Return fallback positions based on typical mobile screen dimensions
            const fallbackPositions = [
                { id: 'tile01', left: '30%', top: '30%', transform: 'translate(-50%, -50%)' },
                { id: 'tile02', left: '72%', top: '30%', transform: 'translate(-50%, -50%)' },
                { id: 'tile03', left: '28%', top: '70%', transform: 'translate(-50%, -50%)' },
                { id: 'tile04', left: '70%', top: '70%', transform: 'translate(-50%, -50%)' }
            ];
            return { positions: fallbackPositions, tileSize, spacing };
        }
        
        const rect = tileWrapper.getBoundingClientRect();
        
        // Calculate center point relative to the wrapper element
        const wrapperCenterX = rect.width / 2;
        const wrapperCenterY = rect.height / 2;
        
        const halfTile = tileSize / 2;
        const halfSpacing = spacing / 2;
        
        // Calculate positions for 2x2 grid around center point (relative to wrapper)
        const positions = [
            // Top-left (tile01)
            {
                id: 'tile01',
                left: `${wrapperCenterX - halfTile - halfSpacing}px`,
                top: `${wrapperCenterY - halfTile - halfSpacing}px`,
                transform: `translate(-50%, -50%)`
            },
            // Top-right (tile02)
            {
                id: 'tile02',
                left: `${wrapperCenterX + halfTile + halfSpacing}px`,
                top: `${wrapperCenterY - halfTile - halfSpacing}px`,
                transform: `translate(-50%, -50%)`
            },
            // Bottom-left (tile03)
            {
                id: 'tile03',
                left: `${wrapperCenterX - halfTile - halfSpacing}px`,
                top: `${wrapperCenterY + halfTile + halfSpacing}px`,
                transform: `translate(-50%, -50%)`
            },
            // Bottom-right (tile04)
            {
                id: 'tile04',
                left: `${wrapperCenterX + halfTile + halfSpacing}px`,
                top: `${wrapperCenterY + halfTile + halfSpacing}px`,
                transform: `translate(-50%, -50%)`
            }
        ];
        
        return { positions, tileSize, spacing };
    };

    // Get tile positions and configuration based on wrapper center point
    const getTilePositionById = (tileId: string, tileSize: number = 160, spacing: number = 20) => {
        const { positions, tileSize: actualTileSize } = getTilePositions(tileSize, spacing);
        const position = positions.find(pos => pos.id === tileId) || null;
        return { position, tileSize: actualTileSize };
    };

    // Debug user state
    useEffect(() => {
        console.log('🔍 Current user state (with fallback):', {
            reduxVisitorId,
            effectiveVisitorId: visitorId,
            fallbackUsed: visitorId !== reduxVisitorId,
            hasVisitorId: !!visitorId,
            visitorIdType: typeof visitorId,
            visitorIdLength: visitorId?.length,
            name,
            email,
            isAuthenticated,
            hasCompletedRegistration
        });
    }, [reduxVisitorId, visitorId, name, email, isAuthenticated, hasCompletedRegistration]);

    // Debug logging for user ID comparison
    useEffect(() => {
        if (zoneStatus?.claimedBy && visitorId) {
            console.log('🔍 User ID comparison:', {
                visitorId,
                claimedBy: zoneStatus.claimedBy,
                isMatch: zoneStatus.claimedBy === visitorId,
                isClaimedByCurrentUser,
                zoneStatus: zoneStatus.status,
                hasClientSideMapping: !!(zoneStatus as any)._clientSideMapped,
                timestamp: new Date().toISOString()
            });
        }
    }, [zoneStatus?.claimedBy, visitorId, isClaimedByCurrentUser, zoneStatus?.status]);

    // Monitor zone status changes for auto tile restoration (per diagram)
    useEffect(() => {
        if (zoneStatus?.payload && isPollingActive) {
            const currentOption = zoneStatus.payload.option;
            console.log('🔍 Zone payload option changed:', {
                option: currentOption,
                selectedTileId,
                isPollingActive,
                shouldRestoreButtons: currentOption === 0 && selectedTileId !== null
            });
            
            // If option is 1-4, ensure the correct tile is selected (prioritize this first)
            if (currentOption >= 1 && currentOption <= 4) {
                const expectedTileId = `tile0${currentOption}`;
                if (selectedTileId !== expectedTileId) {
                    console.log(`🎯 Syncing tile selection to option ${currentOption}`);
                    setSelectedTileId(expectedTileId);
                    setDisplayedTile(expectedTileId);
                    dispatch(setExperienceLive(true));
                }
            }
            
            // If option is 0 (IDLE) and we have a selected tile, restore all tiles
            // BUT add a small delay to avoid race conditions with tile clicks
            else if (currentOption === 0 && selectedTileId !== null) {
                console.log('🔄 Detected option = 0 with selected tile - checking if video actually finished...');
                
                // Add a delay to prevent premature restoration during tile click processing
                setTimeout(() => {
                    // Double-check the option is still 0 after delay
                    if (zoneStatus?.payload?.option === 0 && selectedTileId !== null) {
                        console.log('🔄 Video finished playing - restoring all tiles (option = 0)');
                        
                        // Clear selected state
                        setSelectedTileId(null);
                        setDisplayedTile(null);
                        
                        // Dispatch experience no longer live
                        dispatch(setExperienceLive(false));
                        
                        console.log('✅ All tiles restored - ready for new interaction');
                    } else {
                        console.log('⚠️ Option changed during delay - skipping restoration');
                    }
                }, 1000); // 1 second delay to allow backend processing
            }
        }
    }, [zoneStatus?.payload?.option, selectedTileId, isPollingActive, dispatch]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (zoneTimeoutTimer) {
                clearTimeout(zoneTimeoutTimer);
            }
            if (cooldownTimer) {
                clearTimeout(cooldownTimer);
            }
            if (statusPollingTimerRef.current) {
                console.log('🧹 Component unmounting - cleaning up polling timer ID:', statusPollingTimerRef.current);
                clearInterval(statusPollingTimerRef.current);
                statusPollingTimerRef.current = null;
            }
        };
    }, []); // Empty dependency array - only run on mount/unmount

    // Show popup and start countdown when zone is not available AND user doesn't have active session
    useEffect(() => {
        // Don't show busy popup if timeout popup is already showing
        if (showZoneTimeoutPopup) {
            return;
        }
        
        // Only show popup and start countdown if zone is busy AND user doesn't have an active session AND zone is not claimed by current user
        if (zoneStatus && zoneStatus.status && !isAvailable && !userHasActiveSession && !isClaimedByCurrentUser) {
            if (!isCountdownActive) {
                // Show both popup and start countdown
                setShowZoneBusyPopup(true);
                setBusyPopupCountdown(20); // Reset countdown
                setIsCountdownActive(true);
                console.log('🚫 Zone busy for another user - showing popup + inline message with countdown');
            }
        } else {
            // Don't immediately hide the popup if it's showing an intro message (isCountdownActive = false)
            // This allows the intro popup to stay visible after successful claim
            if (showZoneBusyPopup && !isCountdownActive) {
                console.log('🎬 Keeping intro popup visible - user just claimed zone');
                return; // Don't hide the intro popup
            }
            
            // Zone is available OR user has active session OR zone is claimed by current user - reset everything
            setShowZoneBusyPopup(false);
            setIsCountdownActive(false);
            
            // IMPORTANT: Don't automatically reset user session when zone becomes available
            // The user session should persist even after the zone intro period ends
            // Only reset if the user explicitly times out or manually resets
            if (isAvailable && !userHasActiveSession) {
                // Only reset QR scan flags if user doesn't have an active session
                qrScanClaimInitiated.current = false;
                setQrScanProcessed(false);
                qrScanProcessedRef.current = null;
                console.log('✅ Zone available and no active session - resetting QR scan flags only');
            } else if (userHasActiveSession) {
                console.log('👤 User has active session - maintaining session despite zone availability');
            } else if (isClaimedByCurrentUser) {
                console.log('🔑 Zone claimed by current user - no popup needed');
            }
        }
    }, [zoneStatus, isAvailable, isCountdownActive, userHasActiveSession, showZoneTimeoutPopup, isClaimedByCurrentUser, visitorId, showZoneBusyPopup]);

    // Countdown timer for busy popup (continues even when popup is closed)
    useEffect(() => {
        if (!isCountdownActive || busyPopupCountdown <= 0) {
            if (busyPopupCountdown <= 0 && isCountdownActive) {
                // Countdown finished - reset state and refresh zone status
                setIsCountdownActive(false);
                setShowZoneBusyPopup(false);
                console.log('⏰ Countdown finished - refreshing zone status...');
                // Trigger zone status refresh
                fetchZoneStatus();
            }
            return;
        }

        const timer = setTimeout(() => {
            setBusyPopupCountdown(prev => {
                const newCount = prev - 1;
                console.log(`⏳ Zone busy countdown: ${newCount} seconds remaining`);
                return newCount;
            });
        }, 1000);

        return () => clearTimeout(timer);
    }, [isCountdownActive, busyPopupCountdown, fetchZoneStatus]);

    // Interactive tiles component - just the buttons part
    return (
        <>
            {/* Background Image with Overlay Tiles */}
            <div className="peranakan-design-tile-container mb-6">
                <div className="peranakan-design-tile-wrapper" ref={tileWrapperRef}>
                    <div className="peranakan-design-tile-wrapper-bg">
                        <img
                            src={imagePeranakanTilesBg}
                            alt={t('exhibits.peranakanDesign.patternTitle')}
                        />
                    </div>
                    
                    {/* Tile Hotspots - positioned based on center point */}
                    <div className="absolute inset-0">
                        {tileButtons.map((tile, index) => {
                            const { position, tileSize } = getTilePositionById(tile.id, 180, -20);
                            
                            return (
                                <div
                                    key={tile.id}
                                    id={tile.id}
                                    className={`
                                        peranakan-design-tile absolute has-transition 
                                        ${(() => {
                                            // If zone is claimed by current user, tiles should be fully interactive
                                            if (isClaimedByCurrentUser) {
                                                return 'opacity-100';
                                            }
                                            
                                            // If zone is not claimed by current user, show at 50% opacity
                                            return 'opacity-50';
                                        })()}
                                        ${selectedTileId === tile.id ? '' : ''}
                                    `}
                                    style={{
                                        left: position?.left || '50%',
                                        top: position?.top || '50%',
                                        transform: position?.transform || 'translate(-50%, -50%)',
                                        width: `${tileSize}px`,
                                        height: `${tileSize}px`
                                    }}
                                >
                                    {/* Tile image */}
                                    <div className={`
                                        peranakan-design-tile-bg has-transition
                                        ${selectedTileId === tile.id ? 'opacity-30' : 'opacity-100'}
                                    `}>
                                        <img
                                            src={tile.image}
                                            alt={tile.title}
                                            className={`w-full object-center`}
                                        />
                                    </div>

                                    {/* Selected Content */}
                                    {selectedTileId && (
                                        <div className="peranakan-design-tile-selected-content">
                                            {(() => {
                                                const selectedTile = tileButtons.find(tile => tile.id === selectedTileId);
                                                return (selectedTile && selectedTileId === tile.id) ? (
                                                    <>
                                                        <h4
                                                            className="peranakan-design-tile-selected-content-title"
                                                            dangerouslySetInnerHTML={{ __html: tile.title }}
                                                        />
                                                        <div className={`
                                                            px-3 
                                                            ${selectedTileId === "tile03" ? "max-h-[4.5rem]" : "max-h-[5.5rem]"} 
                                                            overflow-y-auto
                                                        `}>
                                                            <p
                                                                className="peranakan-design-tile-selected-content-description"
                                                                dangerouslySetInnerHTML={{ __html: tile.content }}
                                                            />
                                                        </div>
                                                    </>
                                                ) : null;
                                            })()}
                                        </div>
                                    )}

                                    {/* Round Transparent Hotspot for Tapping */}
                                    <div
                                        onClick={() => {
                                            // Use the enhanced interaction check that considers both zone status and user session
                                            const canInteract = canUserInteract();
                                            
                                            if (canInteract && !buttonCooldown) {
                                                // Map tile IDs to option numbers: tile01=1, tile02=2, tile03=3, tile04=4
                                                const optionNumber = parseInt(tile.id.replace('tile', '').replace(/^0+/, '')) || 0;
                                                
                                                // If clicking the same tile again, unhighlight it (option = 0)
                                                if (selectedTileId === tile.id) {
                                                    handleTileClick(tile.id, tile.title, 0); // Unhighlight
                                                } else {
                                                    handleTileClick(tile.id, tile.title, optionNumber); // Highlight new option
                                                }
                                            }
                                        }}
                                        className={`
                                            peranakan-design-tile-hotspot has-transition 
                                            ${(() => {
                                                // Use the enhanced interaction check
                                                const canInteract = canUserInteract();
                                                
                                                if (!canInteract) {
                                                    return 'opacity-30 cursor-not-allowed pointer-events-none';
                                                }
                                                
                                                // Check if buttons are in cooldown
                                                if (buttonCooldown) {
                                                    return 'cursor-wait pointer-events-none';
                                                }
                                                
                                                // Hotspots are interactive
                                                return 'cursor-pointer';
                                            })()}
                                            ${selectedTileId === tile.id ? '' : ''}
                                        `}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Zone Busy/Intro Popup */}
            {showZoneBusyPopup && (
                <TcPopupR65 
                    handleClose={handleCloseZoneBusyPopup}
                    classNameStyle={`${showZoneBusyPopup ? 'opacity-100' : 'opacity-0'} has-transition`}
                >
                    <div className="text-center">
                        {/* Show different messages based on countdown state */}
                        {isCountdownActive ? (
                            // Zone is used - show countdown
                            <>
                                <h2 className="font-semibold mb-4">{t('exhibitShared.zoneInUseTitle')}</h2>
                                <p>{t('exhibitShared.zoneInUseMessage')}</p>
                                <p className="font-semibold">
                                    {t('exhibitShared.retryIn', { seconds: busyPopupCountdown })}
                                </p>
                            </>
                        ) : (
                            // Zone is playing intro - no countdown
                            <>
                                <h2 className="font-semibold mb-4">{t('exhibitShared.zoneIntroTitle')}</h2>
                                <p>{t('exhibitShared.zoneIntroMessage')}</p>
                            </>
                        )}
                    </div>
                </TcPopupR65>
            )}

            {/* Zone timeout popup */}
            {showZoneTimeoutPopup && (
                <TcPopupR65 
                    hasCloseButton={true}
                    handleClose={handleCloseZoneTimeoutPopup}
                >
                    <div className="text-center">
                        <h2 className="font-semibold mb-4" dangerouslySetInnerHTML={{ __html: t('popup.timeoutTitle') }} />
                        <p dangerouslySetInnerHTML={{ __html: t('popup.timeoutContent') }} />
                    </div>
                </TcPopupR65>
            )}
        </>
    );
}
